import React, { useRef, useEffect, useState } from "react";
import { Eraser } from "lucide-react";
import { api } from "@/lib/api";

// Resolve a stored signature value across every shape clients persist it in:
// web draws to canvas and saves a PNG data URL; mobile/offline may instead store an
// uploaded attachment ({attachment_id}/id/bare-id string), a signed/remote URL, a nested
// {data_url|signature} field, or raw base64 without the data: prefix.
function sigSrc(value) {
  if (!value) return { kind: "none" };
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return { kind: "none" };
    if (s.startsWith("data:")) return { kind: "src", src: s };
    if (/^https?:\/\//i.test(s)) return { kind: "src", src: s };
    if (/^[A-Za-z0-9+/=\s]{100,}$/.test(s)) return { kind: "src", src: `data:image/png;base64,${s.replace(/\s/g, "")}` };
    return { kind: "attachment", id: s };
  }
  if (typeof value === "object") {
    const id = value.attachment_id || value.id || value.aid;
    if (id) return { kind: "attachment", id };
    if (value.url || value.uri) return { kind: "src", src: value.url || value.uri };
    const raw = value.data_url || value.dataUrl || value.signature || value.base64;
    if (typeof raw === "string" && raw) return { kind: "src", src: raw.startsWith("data:") ? raw : `data:image/png;base64,${raw}` };
  }
  return { kind: "none" };
}

// Real signature capture — draws to a canvas (mouse + touch) and emits a PNG
// data URL via onChange. In disabled/view mode it renders the stored signature,
// tolerating any of the shapes above (fetching a signed URL for attachment ids).
export const SignaturePad = ({ value, disabled, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!value);

  const resolved = sigSrc(value);
  const [fetchedSrc, setFetchedSrc] = useState(resolved.kind === "src" ? resolved.src : null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    if (disabled && resolved.kind === "attachment" && resolved.id) {
      api.get(`/attachments/${resolved.id}/view`).then((r) => active && setFetchedSrc(r.data.url)).catch(() => active && setErr(true));
    } else if (resolved.kind === "src") {
      setFetchedSrc(resolved.src);
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, resolved.kind, resolved.id, resolved.src]);

  useEffect(() => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    if (resolved.kind === "src") {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = resolved.src;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.("");
  };

  if (disabled) {
    if (fetchedSrc) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-2">
          <img src={fetchedSrc} alt="Customer signature" className="h-20 w-auto object-contain" data-testid="signature-image" onError={() => setErr(true)} />
        </div>
      );
    }
    if (resolved.kind === "attachment" && !err) {
      return <div className="h-20 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-sm text-slate-400">Loading signature…</div>;
    }
    return (
      <div className="h-20 rounded-lg border-2 border-dashed border-slate-300 bg-white flex items-center justify-center text-sm text-slate-400 italic" data-testid="signature-none">
        No signature
      </div>
    );
  }

  return (
    <div data-testid="signature-pad-wrap">
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full h-24 rounded-lg border-2 border-dashed border-slate-300 bg-white cursor-crosshair"
        style={{ touchAction: "none" }}
        data-testid="signature-pad"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-slate-400">{hasInk ? "Signature captured" : "Sign in the box above"}</span>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"
          data-testid="signature-clear"
        >
          <Eraser className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
